import { useCallback, useEffect, useState } from 'react';
import { useDispatch } from 'react-redux';

import { useLocalize, useTypedSelector } from '../../../shared/hooks';
import { type RootState } from '../../../shared/types';
import { type ReportColumnState } from '../../ViolationsReport/data-layer/violationsReportTypes';
import { useLazyGetReportColumnsQuery } from '../data-layer/entReportsApi';
import {
  entReportsActions,
  type ReportColumnTypes,
} from '../data-store/entReportsSlice';
import { type EntReportTabType } from '../types';

/**
 * useReportColumnsPicker Hook
 *
 * Custom hook for managing report column selection and configuration with dynamic
 * column fetching, selection state management, and bulk operations. Handles column
 * metadata retrieval and user preferences for report customization.
 *
 * @hook
 * @returns {Object} Column management utilities including:
 *   - activeColumns: Array of available columns with selection state
 *   - fetchColumns: Function to fetch columns for a specific report type
 *   - updateColumnSelection: Function to toggle individual column selection
 *   - selectAllColumns: Function to select all available columns
 *   - deselectAllColumns: Function to deselect all non-required columns
 *   - getSelectedColumns: Function to get array of selected column values
 *   - hasSelectedColumns: Function to check if any columns are selected
 *   - isLoading: Boolean indicating if columns are being fetched
 *   - error: Error message if column fetching fails
 *
 * @example
 * const { fetchColumns, updateColumnSelection, activeColumns } = useReportColumnsPicker();
 *
 * // Fetch columns for a report type
 * await fetchColumns('INHERENT_ENTITLEMENT_RISK');
 *
 * // Toggle column selection
 * updateColumnSelection('riskName', true);
 */
const useReportColumnsPicker = () => {
  const dispatch = useDispatch();
  const localize = useLocalize();
  const [getReportColumns, { isLoading }] = useLazyGetReportColumnsQuery();
  const [error, setError] = useState<string | null>(null);

  const { activeColumns = [] } = useTypedSelector(
    (state: RootState) => state.aag.entReports
  );

  const fetchColumns = useCallback(
    async (reportType: string) => {
      try {
        setError(null);
        const grouped = (await getReportColumns(reportType).unwrap()) as Record<
          string,
          ReportColumnState[]
        >;

        const columnsList = Object.values(grouped).flat();

        const modifiedColumnsList: ReportColumnTypes[] = columnsList.map(
          (column) => ({
            label: localize(column.localizationKey),
            value: column.id,
            isChecked: column.isDefault,
            isDisabled: false,
          })
        );

        dispatch(entReportsActions.setReportColumns(modifiedColumnsList));
        dispatch(
          entReportsActions.setColumnsForReportType({
            reportType: reportType as EntReportTabType,
            columns: grouped,
          })
        );
      } catch (err) {
        const errorMessage =
          err instanceof Error ? err.message : 'Failed to fetch columns';

        setError(errorMessage);
      }
    },
    [dispatch, getReportColumns, localize]
  );

  const updateColumnSelection = useCallback(
    (columnValue: string, isChecked: boolean) => {
      if (activeColumns.length === 0) {
        return;
      }

      const updatedColumns = activeColumns.map((col) =>
        col.value === columnValue ? { ...col, isChecked } : col
      );

      dispatch(entReportsActions.setReportColumns(updatedColumns));
    },
    [activeColumns, dispatch]
  );

  const selectAllColumns = useCallback(() => {
    if (activeColumns.length === 0) {
      return;
    }

    const updatedColumns = activeColumns.map((col) => ({
      ...col,
      isChecked: true,
    }));

    dispatch(entReportsActions.setReportColumns(updatedColumns));
  }, [activeColumns, dispatch]);

  const deselectAllColumns = useCallback(() => {
    if (activeColumns.length === 0) {
      return;
    }

    const updatedColumns = activeColumns.map((col) => ({
      ...col,
      isChecked: col.isDisabled, // Keep required columns checked
    }));

    dispatch(entReportsActions.setReportColumns(updatedColumns));
  }, [activeColumns, dispatch]);

  const getSelectedColumns = useCallback(
    () => activeColumns.filter((col) => col.isChecked).map((col) => col.value),
    [activeColumns]
  );

  const hasSelectedColumns = useCallback(
    () => activeColumns.some((col) => col.isChecked),
    [activeColumns]
  );

  // Clear error when columns change
  useEffect(() => {
    if (error && activeColumns.length > 0) {
      setError(null);
    }
  }, [activeColumns, error]);

  return {
    activeColumns,
    fetchColumns,
    updateColumnSelection,
    selectAllColumns,
    deselectAllColumns,
    getSelectedColumns,
    hasSelectedColumns,
    isLoading,
    error,
  };
};

export default useReportColumnsPicker;
